import { Component } from '@theme/component';

/**
 * Marquee Announcement Bar custom element that provides continuous scrolling text.
 * 
 * @extends {Component}
 */
export class AnnouncementBarMarquee extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    
    this.#initializeMarquee();
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  #initializeMarquee() {
    // Find content container in parent element
    const parent = this.parentElement;
    const content = /** @type {HTMLElement} */ (parent?.querySelector('.announcement-bar-marquee__content'));
    if (!content) return;

    const speed = content.dataset.speed || '3';
    const direction = content.dataset.direction || 'left';
    
    content.style.setProperty('--marquee-speed', this.#getSpeedValue(speed));
    
    if (direction === 'right') {
      content.style.animationDirection = 'reverse';
    }
  }

  /**
   * Get speed value in seconds based on speed setting
   * @param {string} speed - Speed setting (1-5)
   * @returns {string} Speed value in seconds
   */
  #getSpeedValue(speed) {
    const speedMap = {
      '1': '40s',  
      '2': '30s',
      '3': '25s',  
      '4': '20s',
      '5': '15s'  
    };
    
    return speedMap[/** @type {keyof typeof speedMap} */ (speed)] || '25s';
  }

  #handleVisibilityChange = () => {
    // Animation will pause/resume automatically based on visibility
    // No manual pause/resume needed as CSS handles it
  };
}

if (!customElements.get('announcement-bar-marquee-component')) {
  customElements.define('announcement-bar-marquee-component', AnnouncementBarMarquee);
}

/**
 * Initialize marquee by duplicating content for seamless loop
 * @param {HTMLElement} element - The marquee container element
 */
function initializeMarquee(element) {
  const content = /** @type {HTMLElement} */ (element.querySelector('.announcement-bar-marquee__content'));
  if (!content) return;

  // Duplicate content for seamless loop (only if not already duplicated)
  if (!content.dataset.duplicated) {
    const children = Array.from(content.children);
    if (children.length > 0) {
      // Clone all children to create seamless loop
      children.forEach(child => {
        const clone = child.cloneNode(true);
        content.appendChild(clone);
      });
      content.dataset.duplicated = 'true';
      
      // Force reflow to ensure proper rendering before animation starts
      void content.offsetWidth;
    }
  }
}

function setupMarquees() {
  const marqueeElements = document.querySelectorAll('.announcement-bar-marquee');
  marqueeElements.forEach((element) => {
    const el = /** @type {HTMLElement} */ (element);
    // Initialize marquee duplication
    initializeMarquee(el);

    // Initialize component if not already present
    if (!el.querySelector('announcement-bar-marquee-component')) {
      const component = document.createElement('announcement-bar-marquee-component');
      el.appendChild(component);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupMarquees);
} else {
  // DOM already loaded, initialize immediately
  setupMarquees();
}
